/**
 * `window.Getdemo.instruction("…")` — a INSTRUÇÃO DA APLICAÇÃO.
 *
 * O texto é escrito por um operador do site do cliente (ex.: cada administradora de condomínio
 * descreve a própria regra de cadastro de contas a pagar) e vira, no backend, um bloco próprio do
 * prompt do agente. É INSTRUÇÃO, não dado: por isso NUNCA viaja como `customParams` — aquele
 * caminho manda o modelo tratar tudo como dado e nunca seguir diretivas, o que anularia a feature.
 *
 * FUNCIONA NA BASE INSTALADA. O snippet oficial cria uma fila GENÉRICA (`window.Getdemo.q`) e o
 * método novo não exige que o cliente troque o snippet: basta drenar `['instruction', args]` da
 * fila que já está lá. Mesmo mecanismo (e mesmo precedente) do `Getdemo.on`/`ensureOnSurface`.
 */

/** Espelha `INSTRUCTION_LIMITS.maxChars` no backend. Aqui é AVISO ao integrador, não controle. */
export const MAX_INSTRUCTION_CHARS = 5000;

let instructionSnapshot: string | null = null;
const listeners: Array<(value: string | null) => void> = [];

/**
 * A fila (`window.Getdemo.q`) é transiente — o SDK a drena logo depois do load. Como o identify,
 * a instrução é guardada num snapshot de módulo que sobrevive à fila esvaziada. Um F5 apaga: a
 * página precisa chamar de novo, exatamente como faz com o identify.
 */
export const getInstructionSnapshot = (): string | null => instructionSnapshot;

/** Registra quem precisa saber de uma chamada TARDIA (login, troca de usuário). */
export const onInstructionChange = (listener: (value: string | null) => void): void => {
  listeners.push(listener);
};

const notify = (): void => {
  for (const listener of listeners) {
    try {
      listener(instructionSnapshot);
    } catch {
      /* um listener não pode derrubar os outros */
    }
  }
};

/**
 * Aplica uma chamada de `Getdemo.instruction(...)`. `null`/`undefined`/vazio LIMPAM (troca de
 * usuário, logout); a última chamada vence.
 *
 * Acima do teto o valor segue assim mesmo, com um `console.warn`: quem descarta é o backend
 * (`sanitizeInstruction`), que é a autoridade única — o payload do WebSocket é montável à mão, e
 * duas validações independentes só criariam duas verdades divergentes. O aviso aqui é DX: o
 * integrador vê o problema no console dele, na hora.
 */
export const applyInstruction = (value: unknown): void => {
  if (value === null || value === undefined) {
    if (instructionSnapshot !== null) {
      instructionSnapshot = null;
      notify();
    }
    return;
  }
  if (typeof value !== 'string') {
    console.warn('[Getdemo] instruction() espera uma string (ou null para limpar).', value);
    return;
  }
  const text = value.trim();
  const next = text.length > 0 ? text : null;
  if (next && next.length > MAX_INSTRUCTION_CHARS) {
    console.warn(
      `[Getdemo] instruction() recebeu ${next.length} caracteres; o limite é ` +
        `${MAX_INSTRUCTION_CHARS} e o servidor vai descartar a instrução deste turno.`
    );
  }
  if (next === instructionSnapshot) return;
  instructionSnapshot = next;
  notify();
};

/**
 * Drena `['instruction', args]` da fila e anexa `Getdemo.instruction` à superfície pública.
 *
 * Idempotente — seguro chamar em todo tick do poll. É preciso re-chamar porque o SDK substitui o
 * objeto `window.Getdemo` INTEIRO durante a própria inicialização, descartando um attach anterior:
 * sem re-arm, a chamada que cair nessa janela se perde.
 */
export const ensureInstructionSurface = (): void => {
  try {
    const w = window as any;
    const g = (w.Getdemo = w.Getdemo || { q: [] });
    if (Array.isArray(g.q)) {
      const remaining: any[] = [];
      for (const entry of g.q) {
        if (Array.isArray(entry) && entry[0] === 'instruction') {
          // `entry[1]` é o objeto `arguments` do stub (`g.q.push([n, arguments])`): array-LIKE,
          // e `Array.isArray` é SEMPRE falso nele. Sem o slice a entrada seria descartada em
          // silêncio — foi exatamente o bug que fez os traits do identify sumirem.
          const args = entry[1] ? Array.prototype.slice.call(entry[1]) : [];
          applyInstruction(args.length > 0 ? args[0] : null);
          continue;
        }
        remaining.push(entry);
      }
      g.q = remaining;
    }
    if (typeof g.instruction === 'function' && g.instruction.__gdInstructionBound) return;
    const bound = (value: unknown) => {
      applyInstruction(value);
    };
    (bound as any).__gdInstructionBound = true;
    g.instruction = bound;
  } catch {
    /* window not available */
  }
};
